import { useCallback, useEffect, useRef } from "react";
import { invoke } from "../lib/bridge";
import { useNotifications } from "./useNotifications";
import { useHighWaterMark } from "./useHighWaterMark";
import { useCableChannel } from "./useCableChannel";
import { notifyIncoming } from "./useNotificationSound";

interface FamilyInfo {
  memberId: number;
}

interface NotificationSocketOptions {
  family: FamilyInfo;
  authToken: string | null;
  apiUrl: string;
  isVisible: boolean;
  /** The channel ID currently being viewed, or null if none. */
  activeChannel: string | null;
  /** Ref to fulfillPendingResyncs — called on WS connect and on resync_request messages. */
  fulfillPendingResyncsRef: React.MutableRefObject<() => void>;
  /** Ref to collectResync — called on resync_fulfilled messages. */
  collectResyncRef: React.MutableRefObject<() => void>;
}

/**
 * Manages the global NotificationChannel WebSocket subscription, a 30 s
 * fallback poll (when the WS is down), initial high-water mark sync, the
 * local member roster (for attributing messages), chat cache pruning, and
 * the doc-name cache for meal-channel labels.
 */
export function useNotificationSocket({
  family,
  authToken,
  apiUrl,
  isVisible,
  activeChannel,
  fulfillPendingResyncsRef,
  collectResyncRef,
}: NotificationSocketOptions): void {
  const { addNotification, clearChannel } = useNotifications();
  const { markRef: lastGlobalChatIdRef, advance: advanceHighWaterMark } = useHighWaterMark();

  // Cache doc names for meal-channel labels in notifications.
  const docNamesRef = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    const refresh = async () => {
      try {
        const docs = await invoke<{ docId: string; title: string }[]>("list_documents", { docType: "meal" });
        const map = new Map<string, string>();
        for (const d of docs) map.set(d.docId, d.title);
        docNamesRef.current = map;
      } catch {}
    };
    refresh();
  }, []);

  // Member id to decrypted name, for attributing messages and notifications.
  const rosterRef = useRef<Record<number, string>>({});
  const loadRoster = useCallback(async () => {
    try {
      rosterRef.current = await invoke<Record<number, string>>("get_member_names");
    } catch {
      // keep last known roster
    }
  }, []);

  // Phase 4b: backfill our own encrypted name on connect, then refresh periodically.
  useEffect(() => {
    if (!authToken) return;
    (async () => {
      try { await invoke("sync_member_name"); } catch {}
      await loadRoster();
    })();
    const interval = setInterval(loadRoster, 60_000);
    return () => clearInterval(interval);
  }, [authToken, loadRoster]);

  // Client-side chat retention: expire cached messages older than chat_retention_days.
  useEffect(() => {
    if (!authToken) return;
    const prune = async () => {
      try {
        const days = await invoke<number>("get_chat_retention");
        await invoke("prune_chat_cache", { days });
      } catch {}
    };
    prune();
    const interval = setInterval(prune, 6 * 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [authToken]);

  // Clear notifications for the channel being viewed.
  useEffect(() => {
    if (activeChannel) clearChannel(activeChannel);
  }, [activeChannel, clearChannel]);

  // Keep stable refs so the WS effect deps stay minimal.
  const activeChannelRef = useRef(activeChannel);
  activeChannelRef.current = activeChannel;
  const addNotificationRef = useRef(addNotification);
  addNotificationRef.current = addNotification;

  const handleNotification = useCallback(
    (m: { id: number; channel: string; senderMemberId: number; createdAt: string }) => {
      if (m.senderMemberId === family.memberId) return;
      if (m.channel === activeChannelRef.current) return;

      advanceHighWaterMark(m.id);

      const label = m.channel === "family" ? "Family" : (docNamesRef.current.get(m.channel) ?? "a meal");
      addNotificationRef.current({
        senderName: rosterRef.current[m.senderMemberId] ?? "Member",
        channel: m.channel,
        channelLabel: label,
        timestamp: m.createdAt,
      });
      notifyIncoming();
    },
    [family.memberId, advanceHighWaterMark],
  );

  const handleNotificationRef = useRef(handleNotification);
  handleNotificationRef.current = handleNotification;

  // WebSocket for global notifications.
  const notifWsConnectedRef = useCableChannel(
    {
      apiUrl,
      authToken,
      onConnect: () => fulfillPendingResyncsRef.current(),
    },
    JSON.stringify({ channel: "NotificationChannel" }),
    (message) => {
      if (message.type === "resync_request") {
        fulfillPendingResyncsRef.current();
        return;
      }
      if (message.type === "resync_fulfilled") {
        collectResyncRef.current();
        return;
      }
      handleNotification({
        id: message.id,
        channel: message.channel,
        senderMemberId: message.sender_member_id,
        createdAt: message.created_at,
      });
    },
  );

  // Set the high-water mark once on mount (runs exactly once; must not re-run on
  // visibility changes so it cannot reset or duplicate the mark).
  useEffect(() => {
    const initialPoll = async () => {
      try {
        const msgs = await invoke<{ id: number; channel: string; senderMemberId: number; createdAt: string }[]>(
          "check_recent_chat", { after: lastGlobalChatIdRef.current }
        );
        if (msgs.length > 0) {
          advanceHighWaterMark(Math.max(...msgs.map(m => m.id)));
        }
      } catch {}
    };
    initialPoll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally empty: run once on mount only

  // Slow fallback poll — only when the WS is disconnected.
  useEffect(() => {
    if (!isVisible) return;
    const interval = setInterval(async () => {
      if (notifWsConnectedRef.current) return;
      try {
        const msgs = await invoke<{ id: number; channel: string; senderMemberId: number; createdAt: string }[]>(
          "check_recent_chat", { after: lastGlobalChatIdRef.current }
        );
        for (const m of msgs) handleNotificationRef.current(m);
      } catch {}
      fulfillPendingResyncsRef.current();
    }, 30000);

    return () => clearInterval(interval);
  }, [isVisible]);
}
